import { ChangeDetectionStrategy, Component, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { ImageCarousel } from '@core/models';
import { Browser, Modal } from '@core/services';
import { filter } from 'rxjs';
import {
  CarouselComponent,
  CarouselIndicatorsComponent,
  CarouselInnerComponent,
  CarouselItemComponent,
  CarouselControlComponent,
} from '@coreui/angular';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { CdnUrlPipe } from '@shared/pipes';

@Component({
  selector: 'app-carousel-full-image',
  imports: [
    CarouselComponent,
    CarouselIndicatorsComponent,
    CarouselInnerComponent,
    CarouselItemComponent,
    CarouselControlComponent,
    RouterLink,
    MatProgressSpinner,
    LazyLoadImageModule,
    CdnUrlPipe,
  ],
  templateUrl: "./carousel-full-image.component.html",
  styleUrl: "./carousel-full-image.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselFullImage {
  private readonly browser = inject(Browser);
  private readonly modalService = inject(Modal);
  private readonly router = inject(Router);

  /** Indice de la imagen seleccionada en la galeria */
  protected readonly imageIndex = signal<number>(0);
  /** Cadena con la url de la imagen a mostrar */
  protected readonly images = signal<string[]>([]);

  protected readonly showContent = signal<boolean>(false);
  protected readonly isLoading = signal<boolean>(false);

  /**
   * Escucha el evento de teclado global y cierra la modal si se presiona Escape
   */
  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (!this.showContent()) return;

    if (event.key === 'Escape') {
      this.onShowContentChange(false);
    }
  }

  /** @ignore */
  constructor() {
    this.modalService
      .getCarouselImage$()
      .pipe(takeUntilDestroyed())
      .subscribe((data) => this.setData(data));

    // Cierra la modal si cambia la ruta
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      if (this.showContent()) {
        this.onShowContentChange(false);
      }
    });
  }

  private setData(data: ImageCarousel): void {
    this.images.set(data.images || []);
    this.imageIndex.set(data.imageIndex || 0);
    this.onShowContentChange(data.showContent ?? false);

    if (data.showContent) {
      this.loadImage(this.imageIndex());
    }
  }

  protected onShowContentChange(option: boolean): void {
    this.showContent.set(option);
    if (this.showContent()) {
      if (this.browser.isBrowser) document.body.classList.add('overflow-hidden');
    } else {
      if (this.browser.isBrowser) document.body.classList.remove('overflow-hidden');
    }
  }

  protected onCurrentSlide(index: number): void {
    this.imageIndex.set(index);
    this.loadImage(index);
  }

  protected onImageLoaded(option: boolean) {
    this.isLoading.set(option);
  }

  private loadImage(index: number) {
    this.isLoading.set(true);
    // Crear una nueva imagen para precargar
    const img = new Image();
    img.onload = () => this.onImageLoaded(false);
    img.onerror = () => this.onImageLoaded(false);

    // Iniciar la precarga
    img.src = this.images()[index];
  }
}
